/* eslint-disable react/no-multi-comp */
import Fade from "@mui/material/Fade";
import { useDeepCompareMemo, useTimeoutEffect } from "@react-hookz/web";
import type { ReactNode } from "react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { SwitchTransition } from "react-transition-group";
import { useSendImpressionEvents } from "scmp-sdk/components/rosetta/hooks";
import type { GA4Event, TriggerPoint } from "scmp-sdk/lib/google-analytics/ga4-types";

import { notEmpty } from "shared/lib/utils";

import { DefaultThemeConfig, ResetAfterSuccessTimeout, VariantRenderMapping } from "./consts";
import type { ContextValue } from "./contexts";
import { NewsletterContextProvider } from "./contexts";
import type { Variant } from "./enums";
import { DisplayComponent } from "./enums";
import { useRecentlySubscribedNewsletters, useSignedUpNewsletter } from "./hooks";
import { getTriggerPoint } from "./sign-up-newsletter/helpers";
import {
  Container,
  FadeContainer,
  StyledFindOutMore,
  StyledSignUpNewsletter,
  StyledSignUpSuccessMessage,
} from "./styles";
import type { DisplayComponentRenderFunctionMap } from "./types";

export type Props = ContextValue &
  React.DetailsHTMLAttributes<HTMLDivElement> & {
    children?: ReactNode;
    /**
     * Will hide the newsletter when all the newsletters has been subscribed
     */
    hideWhenNoNewsletters?: boolean;
    isToggleSuccessMessage?: boolean;
    multipleNewsletters?: boolean;
    onSuccess?: () => void;
    trigger_point?: TriggerPoint;
  };

type AcquisitionNewsletterPopup = Props & {
  campaignName: string;
  variant: Variant.AcquisitionNewsletterPopup;
};

export const NewsletterWidget: React.FunctionComponent<AcquisitionNewsletterPopup | Props> = ({
  campaignName,
  children,
  hideWhenNoNewsletters = false,
  isToggleSuccessMessage = false,
  multipleNewsletters = false,
  newsletterItems,
  onSuccess,
  packageDisplayName = "",
  packageKey,
  trigger_point,
  variant,
  ...props
}) => {
  const signedUpNewsletterItemIds = useSignedUpNewsletter();
  const { isRecentlySubscribed } = useRecentlySubscribedNewsletters();

  const Component = useMemo(() => VariantRenderMapping[variant].renderComponent, [variant]);
  const themeConfig = useMemo(
    () => VariantRenderMapping[variant].config ?? DefaultThemeConfig,
    [variant],
  );

  const [displayComponent, setDisplayComponent] = useState(themeConfig.defaultDisplayComponent);
  const [isResetAfterSuccess, setIsResetAfterSuccess] = useState(false);

  const handleOnSuccess = useCallback(() => {
    if (themeConfig.hasResetAfterSuccess) setIsResetAfterSuccess(true);
    setDisplayComponent(DisplayComponent.SignUpSuccessMessage);
    onSuccess?.();
  }, [onSuccess, themeConfig.hasResetAfterSuccess]);

  const displayComponentMap = useMemo(() => {
    const displayComponentRenderFunctionMap: DisplayComponentRenderFunctionMap = {
      [DisplayComponent.FindOutMore]: p => <StyledFindOutMore {...p} />,
      [DisplayComponent.SignUpNewsletter]: p => (
        <StyledSignUpNewsletter onSuccess={handleOnSuccess} {...p} />
      ),
      [DisplayComponent.SignUpSuccessMessage]: p => <StyledSignUpSuccessMessage {...p} />,
    };
    return Object.entries({
      ...displayComponentRenderFunctionMap,
      ...VariantRenderMapping[variant].hook?.(handleOnSuccess),
    }).reduce(
      (accumulator, [key, renderFunction]) => {
        const castedKey = key as DisplayComponent;
        accumulator[castedKey] = renderFunction();
        return accumulator;
      },
      {} as Record<DisplayComponent, ReactNode>,
    );
  }, [handleOnSuccess, variant]);

  const sanitizedNewsletterItems = useDeepCompareMemo(() => {
    if (!themeConfig.hasSanitizeSubscribedNewsletter) return newsletterItems;
    return newsletterItems.filter(
      newsletter =>
        !signedUpNewsletterItemIds.includes(newsletter.entityId) &&
        !isRecentlySubscribed(newsletter.entityId),
    );
  }, [
    isRecentlySubscribed,
    newsletterItems,
    signedUpNewsletterItemIds,
    themeConfig.hasSanitizeSubscribedNewsletter,
  ]);

  const ga4ImpressionEvent = useCallback<() => GA4Event>(
    () => ({
      action: "imp",
      category: "nl",
      customized_parameters: {
        internal_campaign: campaignName,
        newsletter_name: sanitizedNewsletterItems.map(item => item.name).join("|"),
        trigger_point: trigger_point ?? getTriggerPoint(variant),
        ...(packageDisplayName && { package_name: packageDisplayName }),
      },
      subcategory: "sign_up",
    }),
    [sanitizedNewsletterItems, trigger_point, variant, campaignName, packageDisplayName],
  );

  const { captureTrackImpressionEventTargetElement } = useSendImpressionEvents({
    ga4Event: ga4ImpressionEvent,
  });

  useTimeoutEffect(
    () => setIsResetAfterSuccess(false),
    isResetAfterSuccess ? ResetAfterSuccessTimeout : undefined,
  );

  // Reset display component when packageDisplayName or sanitizedNewsletterItems change
  useEffect(() => {
    if (isToggleSuccessMessage) {
      setDisplayComponent(DisplayComponent.SignUpSuccessMessage);
      return;
    }
    if (!isResetAfterSuccess) {
      const hasNewslettersToShow = notEmpty(sanitizedNewsletterItems) || packageDisplayName;
      setDisplayComponent(
        hasNewslettersToShow
          ? DisplayComponent.SignUpNewsletter
          : themeConfig.defaultDisplayComponent,
      );
      return;
    }
  }, [
    isResetAfterSuccess,
    isToggleSuccessMessage,
    packageDisplayName,
    sanitizedNewsletterItems,
    themeConfig.defaultDisplayComponent,
  ]);

  // Get context value from props
  const {
    dataMatrix,
    findOutMoreButtonDisplayComponent,
    queryParametersForSocialButtons,
    signUpButtonDisplayComponent,
    summaryComponent,
    titleComponent,
  } = props;

  if (hideWhenNoNewsletters && sanitizedNewsletterItems.length === 0) return null;

  return (
    <NewsletterContextProvider
      multipleNewsletters={multipleNewsletters}
      value={{
        campaignName,
        dataMatrix,
        findOutMoreButtonDisplayComponent,
        newsletterItems: sanitizedNewsletterItems,
        packageDisplayName,
        packageKey,
        queryParametersForSocialButtons,
        signUpButtonDisplayComponent,
        summaryComponent,
        titleComponent,
        variant,
      }}
    >
      {children}
      <Component {...props}>
        <Container ref={captureTrackImpressionEventTargetElement}>
          <SwitchTransition mode="out-in">
            <Fade appear={false} key={displayComponent} timeout={500}>
              <FadeContainer>
                {Object.entries(displayComponentMap).map(([key, component]) => (
                  <React.Fragment key={key}>
                    {(key as DisplayComponent) === displayComponent && component}
                  </React.Fragment>
                ))}
              </FadeContainer>
            </Fade>
          </SwitchTransition>
        </Container>
      </Component>
    </NewsletterContextProvider>
  );
};

NewsletterWidget.displayName = "NewsletterWidget";
